import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import RichTextEditor from "../Common/TinyMCE/RichTextEditor";
import "flatpickr/dist/themes/material_green.css";
import { toast } from "react-toastify";
import {
  getExternalEventById,
  addExternalEvent,
  updateExternalEvent,
} from "../../services/externalEventsService";
import { validateEvent } from "../../utils/validation";
import { generateSlug } from "../../utils/cmsUtils";
import { buildEventFormData, mapEventResponse } from "./eventFormUtils";
import { EVENT_STATUS } from "./eventConstants";
import { handleErrors } from "../../utils/errorHandler";
import AiAssistantOffcanvas from "../AI/AiAssistantOffcanvas";
import { EVENT_AI_RESPONSE_FIELD_MAP } from "../AI/AIResponseFieldMap";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";
import { useUserProfile } from "../../hooks/useUserProfile";
import FormSkeleton from "../Common/Loaders/FormSkeleton";
import ButtonLoader from "../Common/Loaders/ButtonLoader";
import ImageUpload from "../Common/ImageUpload/ImageUpload";
import { useMediaLibraryIntegration } from "../MediaLibrary/MediaSourcePicker";
import "../NewsManagement/Articles/article-form.css";

const INITIAL_FORM_STATE = {
  eventName: "",
  eventSlug: "",
  shortDescription: "",
  fullDescription: "",
  eventLocation: "",
  eventsFrom: "",
  eventsTo: "",
  thumbImageFile: null,
  thumbImagePreview: "",
  thumbImageUrl: "",
  isFeatured: false,
  isFeaturedHome: false,
  displayOrder: "",
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
  ctaText: "",
  ctaLink: "",
};

const INITIAL_ERRORS_STATE = {
  eventName: "",
  eventSlug: "",
  shortDescription: "",
  fullDescription: "",
  eventLocation: "",
  eventsFrom: "",
  eventsTo: "",
  thumbImage: "",
  displayOrder: "",
  metaTitle: "",
  metaDescription: "",
  ctaText: "",
  ctaLink: "",
};

const DATETIME_PICKER_OPTIONS = {
  enableTime: true,
  dateFormat: "Y-m-d H:i",
  time_24hr: true,
  monthSelectorType: "static",
};

export const AddEvent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [pageLevelAccessUrl, setPageLevelAccessUrl] = useState("");
  // Per-field Media Library selection, keyed by file field (this session only).
  const [selectedMedia, setSelectedMedia] = useState({});
  const slugManuallyEdited = useRef(false);

  useEffect(() => {
    if (id) {
      setPageLevelAccessUrl("events/update/:id");
    } else {
      setPageLevelAccessUrl("events/add");
    }
  }, [id]);

  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const { isSuperAdmin, loading: profileLoading } = useUserProfile();

  useEffect(() => {
    if (pageAccessData) {
      if (id) {
        if (!pageAccessData.editAccess) {
          navigate("/404-error-page");
        }
      } else if (!pageAccessData.addAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, id, navigate]);

  useEffect(() => {
    const fetchEvent = async () => {
      if (id) {
        setLoading(true);
        try {
          const data = await getExternalEventById(id);
          const mapped = mapEventResponse(data);
          setFormData(mapped);
          slugManuallyEdited.current = true;
        } catch (error) {
          handleErrors(error);
        } finally {
          setLoading(false);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        slugManuallyEdited.current = false;
      }
    };
    fetchEvent();
  }, [id]);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "eventName") {
      setFormData((prev) => ({
        ...prev,
        eventName: value,
        ...(slugManuallyEdited.current
          ? {}
          : { eventSlug: generateSlug(value) }),
      }));
    } else if (name === "eventSlug") {
      slugManuallyEdited.current = true;
      setFormData((prev) => ({ ...prev, eventSlug: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, displayOrder: value }));
      setErrors((prev) => ({ ...prev, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleEditorChange = useCallback((content) => {
    setFormData((prev) => ({
      ...prev,
      fullDescription: content ?? "",
    }));
    setErrors((prev) => ({ ...prev, fullDescription: "" }));
  }, []);

  const handleDateChange = (name, dates, dateStr) => {
    const value = dateStr || "";
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleMediaSelect = (media) => {
    const url = media?.mediaUrl || media?.thumbnailUrl || "";
    if (!url) return;
    setFormData((prev) => ({
      ...prev,
      thumbImageFile: null,
      thumbImagePreview: url,
      thumbImageUrl: url,
    }));
    setSelectedMedia((prev) => ({ ...prev, thumbImageFile: media }));
    setErrors((prev) => ({ ...prev, thumbImage: "" }));
  };

  const handleMediaRemove = () => {
    setFormData((prev) => ({
      ...prev,
      thumbImageFile: null,
      thumbImagePreview: "",
      thumbImageUrl: "",
    }));
    setSelectedMedia((prev) => ({ ...prev, thumbImageFile: null }));
    setErrors((prev) => ({ ...prev, thumbImage: "" }));
  };

  const thumbImageLibrary = useMediaLibraryIntegration({
    media: selectedMedia.thumbImageFile,
    imageUrl: formData.thumbImageUrl,
    defaultKeyword: formData.eventName,
    onSelect: handleMediaSelect,
    onRemove: handleMediaRemove,
  });

  const handleSave = useCallback(
    async (status) => {
      const { valid, errors: validationErrors } = validateEvent(formData);
      setErrors(validationErrors);
      if (!valid) return;

      const payload = buildEventFormData(formData, isEditMode, id, status);

      try {
        setIsButtonDisabled(true);
        const action = isEditMode ? updateExternalEvent : addExternalEvent;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again.",
          );
          return;
        }

        toast.success(
          isSuperAdmin
            ? status === EVENT_STATUS.ACTIVE
              ? "Event published successfully!"
              : "Event saved as draft successfully!"
            : "Event submitted for review successfully!",
        );
        setTimeout(() => {
          navigate("/events");
        }, 1500);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formData, isEditMode, id, navigate, isSuperAdmin],
  );

  if (loading && isEditMode) {
    return <FormSkeleton />;
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">
              {isEditMode ? "Update Event" : "Add Event"}
            </h4>
            <div className="page-title-right d-flex align-items-center">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/">
                    <i className="ri-home-2-fill"></i>
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/events">Events</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isEditMode ? `Update #${id}` : "Add"}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <form
        className="article-form"
        onSubmit={(e) => {
          e.preventDefault();
        }}
        method="POST"
      >
        <div className="row">
          <div className="col-lg-8">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="card-section-title">
                  <span className="card-section-title__icon">
                    <i className="ri-calendar-event-line"></i>
                  </span>
                  Basic Information
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-12">
                    <div className="mb-3">
                      <label htmlFor="eventName" className="form-label">
                        Event Name <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="eventName"
                        id="eventName"
                        placeholder="Enter event name"
                        value={formData.eventName}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.eventName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.eventName && (
                        <div className="invalid-feedback">
                          {errors.eventName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="eventSlug" className="form-label">
                        Event Slug <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="eventSlug"
                        id="eventSlug"
                        placeholder="event-slug"
                        value={formData.eventSlug}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.eventSlug ? "is-invalid" : ""
                        }`}
                      />
                      {errors.eventSlug && (
                        <div className="invalid-feedback">
                          {errors.eventSlug}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="eventLocation" className="form-label">
                        Event Location <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="eventLocation"
                        id="eventLocation"
                        value={formData.eventLocation}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.eventLocation ? "is-invalid" : ""
                        }`}
                      />
                      {errors.eventLocation && (
                        <div className="invalid-feedback">
                          {errors.eventLocation}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-3">
                      <label htmlFor="shortDescription" className="form-label">
                        Short Description{" "}
                        <span className="required-field">*</span>
                      </label>
                      <textarea
                        name="shortDescription"
                        id="shortDescription"
                        rows="4"
                        value={formData.shortDescription}
                        onChange={handleInputChange}
                        maxLength={250}
                        className={`form-control ${
                          errors.shortDescription ? "is-invalid" : ""
                        }`}
                      />
                      {errors.shortDescription && (
                        <div className="invalid-feedback">
                          {errors.shortDescription}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-0">
                      <label htmlFor="fullDescription" className="form-label">
                        Full Description{" "}
                        <span className="required-field">*</span>
                      </label>
                      <div className="editor-wrap">
                        <RichTextEditor
                          value={formData.fullDescription}
                          onEditorChange={handleEditorChange}
                          defaultKeyword={formData.title}
                        />
                      </div>
                      {errors.fullDescription && (
                        <div
                          className="text-danger mt-1"
                          style={{ fontSize: ".875em" }}
                        >
                          {errors.fullDescription}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">
                <h5 className="card-section-title">
                  <span className="card-section-title__icon">
                    <i className="ri-image-line"></i>
                  </span>
                  Media
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-4">
                  <div className="col-12">
                    <ImageUpload
                      label={
                        <>
                          Thumb Image
                          <span
                            className="text-muted fw-normal"
                            style={{ fontSize: "0.8125rem" }}
                          >
                            {" "}
                            (Recommended: 950 × 520 px)
                          </span>
                        </>
                      }
                      required
                      previewUrl={formData.thumbImagePreview}
                      error={errors.thumbImage}
                      {...thumbImageLibrary.uploadEnhancements}
                    />
                    {thumbImageLibrary.modalSlot}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="card-section-title">
                  <span className="card-section-title__icon">
                    <i className="ri-settings-3-line"></i>
                  </span>
                  Display Settings
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label d-block">Is Featured</label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isFeatured"
                      name="isFeatured"
                      checked={formData.isFeatured}
                      onChange={handleToggleChange}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label d-block">Featured Home</label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isFeaturedHome"
                      name="isFeaturedHome"
                      checked={formData.isFeaturedHome}
                      onChange={handleToggleChange}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="displayOrder" className="form-label">
                    Display Order{" "}
                    <span className="text-muted fw-normal">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    name="displayOrder"
                    id="displayOrder"
                    min="1"
                    step="1"
                    value={formData.displayOrder}
                    onChange={handleDisplayOrderChange}
                    onKeyDown={handleDisplayOrderKeyDown}
                    className={`form-control ${
                      errors.displayOrder ? "is-invalid" : ""
                    }`}
                  />
                  {errors.displayOrder && (
                    <div className="invalid-feedback">
                      {errors.displayOrder}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="card-section-title">
                  <span className="card-section-title__icon">
                    <i className="ri-calendar-event-line"></i>
                  </span>
                  Event Settings
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label htmlFor="eventsFrom" className="form-label">
                        Events From <span className="required-field">*</span>
                      </label>
                      <Flatpickr
                        className={`form-control ${
                          errors.eventsFrom ? "is-invalid" : ""
                        }`}
                        placeholder="Start date & time"
                        value={formData.eventsFrom}
                        options={DATETIME_PICKER_OPTIONS}
                        onChange={(dates, dateStr) =>
                          handleDateChange("eventsFrom", dates, dateStr)
                        }
                      />
                      {errors.eventsFrom && (
                        <div className="invalid-feedback d-block">
                          {errors.eventsFrom}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label htmlFor="eventsTo" className="form-label">
                        Events To <span className="required-field">*</span>
                      </label>
                      <Flatpickr
                        className={`form-control ${
                          errors.eventsTo ? "is-invalid" : ""
                        }`}
                        placeholder="End date & time"
                        value={formData.eventsTo}
                        options={DATETIME_PICKER_OPTIONS}
                        onChange={(dates, dateStr) =>
                          handleDateChange("eventsTo", dates, dateStr)
                        }
                      />
                      {errors.eventsTo && (
                        <div className="invalid-feedback d-block">
                          {errors.eventsTo}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="card-section-title">
                  <span className="card-section-title__icon">
                    <i className="ri-search-eye-line"></i>
                  </span>
                  SEO Settings
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="metaTitle" className="form-label">
                    Meta Title
                  </label>
                  <input
                    type="text"
                    name="metaTitle"
                    id="metaTitle"
                    value={formData.metaTitle}
                    onChange={handleInputChange}
                    className={`form-control ${
                      errors.metaTitle ? "is-invalid" : ""
                    }`}
                  />
                  {errors.metaTitle && (
                    <div className="invalid-feedback">{errors.metaTitle}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label htmlFor="metaDescription" className="form-label">
                    Meta Description
                  </label>
                  <textarea
                    name="metaDescription"
                    id="metaDescription"
                    rows="4"
                    value={formData.metaDescription}
                    onChange={handleInputChange}
                    className={`form-control ${
                      errors.metaDescription ? "is-invalid" : ""
                    }`}
                  />
                </div>
                <div className="mb-0">
                  <label htmlFor="metaKeywords" className="form-label">
                    Meta Keywords
                  </label>
                  <textarea
                    name="metaKeywords"
                    id="metaKeywords"
                    rows="3"
                    value={formData.metaKeywords}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>

            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="card-section-title">
                  <span className="card-section-title__icon">
                    <i className="ri-megaphone-line"></i>
                  </span>
                  CTA Config
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="ctaText" className="form-label">
                    CTA Text <span className="required-field">*</span>
                  </label>
                  <input
                    type="text"
                    name="ctaText"
                    id="ctaText"
                    placeholder="e.g. Register Now"
                    value={formData.ctaText}
                    onChange={handleInputChange}
                    className={`form-control ${
                      errors.ctaText ? "is-invalid" : ""
                    }`}
                  />
                  {errors.ctaText && (
                    <div className="invalid-feedback">{errors.ctaText}</div>
                  )}
                </div>
                <div className="mb-0">
                  <label htmlFor="ctaLink" className="form-label">
                    CTA Link <span className="required-field">*</span>
                  </label>
                  <input
                    type="text"
                    name="ctaLink"
                    id="ctaLink"
                    placeholder="https://example.com/register"
                    value={formData.ctaLink}
                    onChange={handleInputChange}
                    className={`form-control ${
                      errors.ctaLink ? "is-invalid" : ""
                    }`}
                  />
                  {errors.ctaLink && (
                    <div className="invalid-feedback">{errors.ctaLink}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <div className="form-actions">
              {isSuperAdmin ? (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary btn-action-primary"
                    disabled={isButtonDisabled || profileLoading}
                    onClick={() => handleSave(EVENT_STATUS.ACTIVE)}
                  >
                    {isButtonDisabled ? (
                      <ButtonLoader label="Publishing" />
                    ) : (
                      "Publish"
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-light"
                    disabled={isButtonDisabled || profileLoading}
                    onClick={() => handleSave(EVENT_STATUS.DRAFT)}
                  >
                    {isButtonDisabled ? (
                      <ButtonLoader label="Saving" />
                    ) : (
                      "Save Draft"
                    )}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary btn-action-primary"
                  disabled={isButtonDisabled || profileLoading}
                  onClick={() => handleSave(EVENT_STATUS.DRAFT)}
                >
                  {isButtonDisabled ? (
                    <ButtonLoader label="Submitting" />
                  ) : (
                    "Submit For Review"
                  )}
                </button>
              )}
              <Link to="/events" className="btn btn-danger">
                Cancel
              </Link>
            </div>
          </div>
        </div>
      </form>

      <AiAssistantOffcanvas
        pageLink={
          pageLevelAccessUrl ||
          (isEditMode ? "events/update/:id" : "events/add")
        }
        formData={formData}
        setFormData={setFormData}
        fieldMap={EVENT_AI_RESPONSE_FIELD_MAP}
        editorFields={["fullDescription"]}
        editorHandlers={{ fullDescription: handleEditorChange }}
      />
    </>
  );
};
